import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Button, Modal, FormCheck } from 'react-bootstrap';
import ClipLoader from 'react-spinners/ClipLoader';
import { ILineItem } from '../../typings/LineItem';
import Model from '../../model/ShopKeeper.model';
import Label from '../Label';
import toastr from '../../../toastr';

interface IProps {
  container: Object;
  hide: Function;
  itemName: string;
  lineItem: ILineItem;
  model: Model;
  show: boolean;
}

const RemoveROLineItemModal: FunctionComponent<IProps> = ({ container, hide, itemName, lineItem, model, show }) => {
  // Locked operations cannot have anything issued nor returned.
  // There's no reason to check for a voided state on return since all lines must be removed before voiding an op anyways.
  const locked = model.activeRepairOrderDetails.operationDetails[lineItem.roDetail!.operationNumber].locked;

  const isComment = lineItem.comment;

  const inputQtyRef = useRef<HTMLInputElement>(null);
  const inputReturnPasswordRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dirtyCoreReturn, setDirtyCoreReturn] = useState(false);

  // Pre-select the seeded quantity for fast edit
  useEffect(() => {
    // This input does not exist on comment line items.
    if (inputQtyRef && inputQtyRef.current) {
      const el = inputQtyRef.current;
      el.focus();
      el.setSelectionRange(0, el.value.length);
    }
  }, []);

  const process = async () => {
    if (model.returnPPCAuthRequired) {
      if (!model.validatePPCReturnPassword(inputReturnPasswordRef.current!.value)) {
        // validate pops toaster on fail
        setIsProcessing(false);
        return;
      }
    }

    let qty = 1; // default for comments, default parts, etc.

    if (!isComment) {
      qty = parseInt(inputQtyRef.current!.value);

      if (dirtyCoreReturn) {
        if (Number.isNaN(qty) || qty < 1 || qty > lineItem.detail!.coreQuantity) {
          toastr.error('Invalid Qauntity', `Enter a qty between 1 and ${lineItem.detail!.coreQuantity}`);
          return;
        }
      } else {
        if (Number.isNaN(qty) || qty < 1 || qty > Math.abs(lineItem.quantity)) {
          toastr.error('Invalid Qauntity', `Enter a qty between 1 and ${Math.abs(lineItem.quantity)}`);
          return;
        }
      }
    }

    setIsProcessing(true);
    const success = await model.removeROLineItem(lineItem, qty, dirtyCoreReturn);

    if (success) {
      hide();
    } else {
      // an error popped - re-enable controls to try again or cancel
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      backdrop="static"
      size="sm"
      container={container as any}
      dialogClassName="sk-removeROLineItemModal"
      onHide={hide as any}
      show={show}
    >
      <Modal.Header>
        <Modal.Title>Confirm Remove/Return</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {`${lineItem.roDetail!.operationNumber}.${lineItem.roDetail!.lineNumber}`}: {itemName}
        {!isComment && (
          <>
            <br />
            QTY: {lineItem.quantity}
            <hr />
            {lineItem.detail && lineItem.detail.coreAttached && (
              <div className="sk-returnDirtyCores">
                <FormCheck inline={true}>
                  <FormCheck.Input
                    type="checkbox"
                    id="return-dirty-cores-only"
                    className="sk-useMaterialInput mt-3"
                    checked={dirtyCoreReturn}
                    onChange={(evt: any) => setDirtyCoreReturn(evt.target.checked)}
                  />
                  <FormCheck.Label htmlFor="return-dirty-cores-only">Return dirty cores only?</FormCheck.Label>
                </FormCheck>
                <div className="sk-returnDirtyCoreOutstanding">
                  {lineItem.detail.coreQuantity} of {lineItem.quantity} dirty cores outstanding
                </div>
              </div>
            )}
            <div className="sk-roRemoveQty">
              <Label>Qty to Remove</Label>
              <input defaultValue={Math.abs(lineItem.quantity)} type="text" ref={inputQtyRef} />
            </div>
          </>
        )}
        {model.canClerkPPCIssueReturn && model.returnPPCAuthRequired && (
          <div className="sk-returnPassword">
            <Label>Return Password</Label>
            <input type="password" ref={inputReturnPasswordRef} />
          </div>
        )}
        {locked && <div className="sk-returnLocked">This operation is locked.</div>}
        {!locked && !model.canClerkPPCIssueReturn && (
          <div className="sk-returnLocked">PPC not permitted to issue return.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={isProcessing} onClick={() => hide()}>
          Cancel
        </Button>
        <Button variant="danger" disabled={isProcessing || locked || !model.canClerkPPCIssueReturn} onClick={process}>
          {isProcessing ? (
            <>
              <ClipLoader size={13} color="white" /> PROCESSING
            </>
          ) : (
            'REMOVE'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RemoveROLineItemModal;
